import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import WistiaPlayer from "../components/WistiaPlayer";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const VerticalVideo = ({ caption, mediaId }) => (
  <article className="flex flex-col items-center" data-testid={`video-${mediaId}`}>
    <div className="w-full max-w-[280px]">
      <WistiaPlayer mediaId={mediaId} aspect={0.5625} />
    </div>
    <span className="font-condensed uppercase tracking-widest text-[12px] text-lime mt-4">
      {caption}
    </span>
  </article>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[44ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-dodgers/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-dodgers whitespace-nowrap"
      style={{ fontSize: "clamp(1.75rem, 3.4vw, 2.85rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-80 max-w-[22ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

const QualBlock = ({ title, body }) => (
  <div>
    <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-dodgers">
      {title}
    </h4>
    <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
      {body}
    </p>
  </div>
);

const KappaCoop = () => {
  const idx = WORKS.findIndex((w) => w.to === "/works/kappa-x-coop");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = WORKS[(idx + 1) % WORKS.length];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Kappa × Coop", url: "/works/kappa-x-coop" },
  ]);

  return (
    <>
      <Seo
        title="Kappa × Coop: campagna vita attiva e sostenibilità | OnSports Case"
        description="Campagna content Kappa × Coop per Instagram e TikTok sulla collaborazione brand vita attiva e sostenibilità. OnSports Content & IP Studio, 2020."
        path="/works/kappa-x-coop"
        image="https://fast.wistia.com/embed/medias/148oeb8nf4/swatch"
        jsonLd={breadcrumbJsonLd}
      />

      {/* HERO */}
      <CaseStudyHero
        imagePath="/images/works/kappa-x-coop/hero.gif"
        imageAlt="Kappa × Coop Lombardia — Sport inizia con K"
        eyebrow="● Case study · Content · Performance · TikTok"
        titleLines={["Sport inizia con K.", "Il racconto, su TIKTOK."]}
        highlightWord="TIKTOK."
        subtitle="Abbiamo accompagnato il co-marketing tra Coop Lombardia e Kappa con un linguaggio nativo Gen Z: dalla creatività dei video alla performance marketing su TikTok Ads. Il risultato: oltre 623mila impression e 34mila interazioni in dieci settimane."
        meta="2023  ·  Clienti — Coop Lombardia × Kappa  ·  Categoria — Content & Performance"
      />

      {/* OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    "Sport inizia con K!" è la collection-promotion che ha unito Coop
                    Lombardia e Kappa nell'autunno 2023. Una meccanica di raccolta
                    bollini — uno ogni 15 euro di spesa, dal 31 agosto all'8 novembre —
                    che ha permesso ai soci Coop di portare a casa una collezione di
                    otto prodotti sportivi a marchio Kappa, dal borsone al pallone,
                    dall'accappatoio al beauty case. Tutti realizzati con percentuali
                    di materiale riciclato fra il 67% e il 79%.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    L'operazione metteva insieme due mondi che raramente si parlano: la
                    grande distribuzione organizzata e lo sportswear di tradizione. Per
                    funzionare doveva uscire dal linguaggio del volantino e raggiungere
                    un pubblico che il volantino non lo legge. Per questo Coop ha
                    chiesto a OnSports di costruire la narrazione social della
                    collezione — con un focus chiaro: TikTok.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Progetto">Sport inizia con K!</SchedaRow>
                    <SchedaRow label="Clienti">Coop Lombardia · Kappa</SchedaRow>
                    <SchedaRow label="Pilastro">
                      Cultural Impact · Performance Marketing
                    </SchedaRow>
                    <SchedaRow label="Ruolo">
                      Ideazione creativa · Produzione video<br />
                      Advertising performance su TikTok Ads
                    </SchedaRow>
                    <SchedaRow label="Periodo">31 agosto — 8 novembre 2023</SchedaRow>
                    <SchedaRow label="Sostenibilità">
                      Collezione in materiale riciclato 67–79%
                    </SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Far parlare la GDO la lingua di TikTok.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Il punto di partenza era un asset commerciale solido: una collezione
                  con un nome forte, un partner riconoscibile come Kappa, una meccanica
                  promozionale chiara. Ma il pubblico naturale di una raccolta bollini
                  Coop e il pubblico di TikTok non si sovrappongono spontaneamente.
                  Servivano contenuti pensati nativamente per il feed verticale — codici
                  visivi, ritmo, tone — capaci di trattenere uno scroll Gen Z su
                  un'iniziativa di grande distribuzione, senza tradire né il
                  posizionamento di Kappa né l'identità di Coop.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* I VIDEO (4 verticali 9:16) */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-video">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-14">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>I video</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Quattro pezzi. Una sola voce.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch]">
                  Abbiamo prodotto quattro video short-form pensati come unità native di
                  TikTok e Reels: formati 9:16, hook immediati, ritmo costruito per il
                  consumo verticale, codici visivi sportwear-meet-retail. Gli stessi
                  asset hanno alimentato sia la distribuzione organica sia la campagna a
                  pagamento.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 justify-items-center">
            <RevealOnScroll delay={0.05}>
              <VerticalVideo caption="Video 01" mediaId="148oeb8nf4" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <VerticalVideo caption="Video 02" mediaId="f79qdz71yo" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <VerticalVideo caption="Video 03" mediaId="5nwbxlnknz" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <VerticalVideo caption="Video 04" mediaId="uxwkssc50s" />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Dalla creatività alla conversione.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
                  Un'unica regia per tre fasi: concept creativo costruito sui codici
                  TikTok e Reels, produzione video pensata per essere efficace già nei
                  primi tre secondi di scroll, attivazione paid mirata su audience
                  profilate per intercettare un pubblico Gen Z e Millennial in
                  Lombardia.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Ideazione creativa"
                body="Direzione creativa, concept dei quattro video, definizione del tono comunicativo a metà strada tra il linguaggio Kappa e l'ecosistema di valori Coop. Storyboard, casting e selezione delle location coerenti con un mood urbano e sportivo accessibile."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Produzione video"
                body="Produzione integrale: riprese, fotografia di scena, direzione artistica, montaggio e post in formato 9:16 nativo. Sottotitoli always-on per il consumo silenzioso, sound design tarato sui pattern di engagement di TikTok."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="TikTok Ads"
                body="Pianificazione e attivazione delle campagne su TikTok Advertising: prospecting su audience di interesse sport e lifestyle in Lombardia, retargeting sulle visualizzazioni dei primi giorni, ottimizzazione progressiva di creatività e segmenti nelle dieci settimane di campagna."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* IMPATTO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">I numeri della campagna.</h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="623.000+" label="Impressions TikTok" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="34.000+" label="Interazioni generate" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="~5,5%" label="Engagement rate sopra benchmark di settore" />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            Dati aggregati della campagna paid su TikTok Ads nel periodo 31 agosto — 8
            novembre 2023. L'engagement rate è calcolato sul rapporto interazioni /
            impression ed è significativamente superiore alle medie di benchmark TikTok
            per campagne brand del segmento retail e sportswear.
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <QualBlock
                title="Linguaggio nativo"
                body="I quattro video sono stati costruiti come contenuti TikTok-first, non come adattamenti di asset pubblicitari preesistenti. Questo ha permesso un engagement rate sopra la media di settore senza forzare il tono dei due brand."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <QualBlock
                title="Pubblico ampliato"
                body="La campagna ha portato l'operazione promozionale fuori dal perimetro tradizionale dei comunicati Coop e dei canali sport mainstream, intercettando un'audience Gen Z e Millennial difficile da raggiungere con i media classici della grande distribuzione."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <QualBlock
                title="Sostenibilità in primo piano"
                body="Il racconto ha valorizzato in modo naturale la componente di materiale riciclato della collezione, allineando la comunicazione agli impegni ESG di Coop senza appesantire il messaggio."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* CREDITS */}
      <section className="py-20 md:py-28 bg-dodgers text-galactico" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Clienti">Coop Lombardia · Kappa</SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Ideazione creativa, produzione video,<br />
                  advertising performance su TikTok
                </SchedaRow>
                <SchedaRow label="Servizi">
                  Content &amp; IP Studio · Performance Marketing &amp; Growth
                </SchedaRow>
                <SchedaRow label="Anno">2023</SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10" data-testid="section-cta-finale">
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[26ch]">
              Hai un'operazione di co-marketing da raccontare? Costruiamo insieme il suo{" "}
              <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[60ch]">
              Lavoriamo con brand e retailer che vogliono uscire dalle convenzioni del
              proprio settore e parlare ai pubblici che contano davvero. Raccontaci la
              tua iniziativa.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default KappaCoop;
